import React, { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import {
  createSectionWorkType,
  getKnownWorkTypeNames,
} from "../../../../api/projectProgressApi";
import "../AddWorkTypeModal/AddWorkTypeModal.css";
import "./AddMilestoneModal.css";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? "#2a5e38" : "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "#2a5e38" : "#9ca3af" },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#111827",
    fontSize: 14,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1100,
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected
      ? "#2a5e38"
      : state.isFocused
        ? "#eef5f0"
        : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

const AddMilestoneModal = ({
  open,
  projectId,
  sectionId,
  existingNames = [],
  onClose,
  onCreated,
}) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [planDate, setPlanDate] = useState("");
  const [loadingNames, setLoadingNames] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const existingSet = useMemo(
    () => new Set(existingNames.map(normalizeName)),
    [existingNames],
  );

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setPlanDate("");
    setSubmitting(false);
    setNameError("");
    setSubmitError("");
    setLoadError("");
    setOptions([]);

    let cancelled = false;
    (async () => {
      try {
        setLoadingNames(true);
        const data = await getKnownWorkTypeNames(projectId, sectionId, {
          isMilestone: true,
        });
        if (cancelled) return;
        const names = Array.isArray(data?.names) ? data.names : [];
        const existing = new Set(existingNames.map(normalizeName));
        setOptions(
          names
            .filter((name) => name && !existing.has(normalizeName(name)))
            .map((name) => ({ value: name, label: name })),
        );
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Не удалось загрузить список вех");
        }
      } finally {
        if (!cancelled) setLoadingNames(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, projectId, sectionId, existingNames]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const trimmedName = String(selected?.value || "").trim();
  const isDuplicate =
    Boolean(trimmedName) && existingSet.has(normalizeName(trimmedName));
  const canSubmit =
    Boolean(trimmedName) && Boolean(planDate) && !isDuplicate && !submitting;

  const milestoneSelectStyles = {
    ...selectStyles,
    control: (provided, state) => ({
      ...selectStyles.control(provided, state),
      borderColor: nameError
        ? "#dc2626"
        : state.isFocused
          ? "#2a5e38"
          : "#d1d5db",
      "&:hover": {
        borderColor: nameError
          ? "#dc2626"
          : state.isFocused
            ? "#2a5e38"
            : "#9ca3af",
      },
    }),
  };

  const isValidNewOption = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (!trimmed) return false;
    const key = normalizeName(trimmed);
    if (existingSet.has(key)) return false;
    if (selected && normalizeName(selected.value) === key) return false;
    if (options.some((opt) => normalizeName(opt.value) === key)) return false;
    return true;
  };

  const handleSelectChange = (value) => {
    setSelected(value);
    setSubmitError("");
    if (value && existingSet.has(normalizeName(value.value))) {
      setNameError("Такая веха уже есть в этом разделе!");
    } else {
      setNameError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmedName) {
      setNameError("Укажите название вехи");
      return;
    }
    if (existingSet.has(normalizeName(trimmedName))) {
      setNameError("Такая веха уже есть в этом разделе!");
      return;
    }
    if (!planDate) {
      setSubmitError("Укажите плановую дату");
      return;
    }
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setSubmitError("");
      const created = await createSectionWorkType(projectId, sectionId, {
        name: trimmedName,
        is_milestone: true,
        unit_weight_percent: 0,
        plan_start_date: planDate,
        plan_end_date: planDate,
        completion_percent: 0,
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Не удалось добавить веху");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
      <div
        className="add-work-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-milestone-modal-title"
      >
        <h2
          id="add-milestone-modal-title"
          className="add-work-type-modal__title"
        >
          Добавление новой вехи
        </h2>

        <div className="add-work-type-modal__hint">
          <span className="add-work-type-modal__hint-icon" aria-hidden>
            ⚠
          </span>
          <p>Это контрольная точка без трудозатрат и удельного веса.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <label
            className="add-work-type-modal__label"
            htmlFor="add-milestone-name"
          >
            Выберите веху или создайте новую
          </label>
          <CreatableSelect
            inputId="add-milestone-name"
            isClearable
            isSearchable
            isDisabled={submitting}
            isLoading={loadingNames}
            options={options}
            value={selected}
            onChange={handleSelectChange}
            styles={milestoneSelectStyles}
            menuPortalTarget={document.body}
            placeholder="Начните вводить название..."
            noOptionsMessage={() =>
              loadingNames
                ? "Загрузка..."
                : "Нет вариантов — введите название, чтобы создать"
            }
            formatCreateLabel={(input) => `Создать «${input.trim()}»`}
            isValidNewOption={isValidNewOption}
            onCreateOption={(inputValue) => {
              const name = String(inputValue || "").trim();
              if (!isValidNewOption(name)) {
                if (existingSet.has(normalizeName(name))) {
                  setNameError("Такая веха уже есть в этом разделе!");
                }
                return;
              }
              const option = { value: name, label: name, __isNew__: true };
              setOptions((prev) => [...prev, option]);
              setSelected(option);
              setNameError("");
              setSubmitError("");
            }}
          />
          {nameError && (
            <p className="add-work-type-modal__error add-milestone-modal__error">
              <span aria-hidden>⚠</span>
              {nameError}
            </p>
          )}
          {loadError && (
            <p className="add-work-type-modal__error">{loadError}</p>
          )}

          <label
            className="add-work-type-modal__label add-milestone-modal__label-spaced"
            htmlFor="add-milestone-plan-date"
          >
            Плановая дата
          </label>
          <div className="add-milestone-modal__field">
            <input
              id="add-milestone-plan-date"
              type="date"
              className="add-milestone-modal__input"
              value={planDate}
              onChange={(e) => {
                setPlanDate(e.target.value);
                setSubmitError("");
              }}
              disabled={submitting}
            />
          </div>

          {submitError && (
            <p className="add-work-type-modal__error">{submitError}</p>
          )}

          <div className="add-work-type-modal__actions">
            <button
              type="button"
              className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-work-type-modal__btn add-work-type-modal__btn--submit"
              disabled={!canSubmit}
            >
              {submitting ? "Добавление..." : "Добавить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddMilestoneModal;
